// ClientHub Capabilities data layer (Team & Admin §2 — /hub/admin/capabilities).
//
// Read-only, frontend-direct per docs/CLIENTHUB_KNOWLEDGE_BASE.md §7. The screen
// answers "what the company can do, and where it is fragile" — derived from
// 102,000 completed tasks; nobody typed a profile, and a derived one cannot go
// stale. Every read resolves auth.uid() itself; the database is the wall, the
// capability check on the page is a convenience (00_CONTRACT.md §6).
//
// Objects (verified live 2026-09-11 — see scripts/_probe-admin-spec-verify.js
// in the BFF repo):
//   v_capability_coverage — capability_key · label · category · people · agents ·
//                           times_done · single_point_of_failure · nobody_recent
//   mv_member_capability  — who can do what, with depth and recency
//   fn_who_can(p_capability text?, p_client_id uuid?, p_limit int?) → jsonb
//   ops_capabilities       — the catalogue (capability_key · label · category ·
//                           description · is_active)
//
// Column drift: the real column on v_capability_coverage is `capability_key` +
// `label` (the spec said `capability`). Bound to the real names.
//
// Modelled on api/hub/now.js: throw-on-error, unwrap the `{error:true, message}`
// gate envelope, label errors by name, one jittered retry on a transport (not
// database) failure. HUB_STUB is a local-dev-only fixtures override
// (REACT_APP_HUB_STUB=1).

import { supabase } from "../../lib/supabase";
import vCapabilityCoverage from "../../components/hub/capabilities/__fixtures__/v_capability_coverage.json";
import mvMemberCapability from "../../components/hub/capabilities/__fixtures__/mv_member_capability.json";
import opsCapabilities from "../../components/hub/capabilities/__fixtures__/ops_capabilities.json";
import fnWhoCan from "../../components/hub/capabilities/__fixtures__/fn_who_can.json";

const HUB_STUB =
  typeof process !== "undefined" &&
  !!process.env &&
  process.env.REACT_APP_HUB_STUB === "1";

// A refused HTTP/2 stream is a transport failure, not a database one — retry
// once. A Postgres error / RLS refusal / bad parameter carries a `code` and
// fails on the first attempt, where it belongs. (Mirrors api/hub/now.js.)
const TRANSPORT_FAILURE =
  /failed to fetch|networkerror|load failed|err_http2|connection closed/i;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isTransportFailure = (err) =>
  !err?.code && TRANSPORT_FAILURE.test(err?.message || "");

async function capRpc(name, params, label) {
  const attempt = async () => {
    try {
      return await supabase.rpc(name, params);
    } catch (e) {
      return { data: null, error: e };
    }
  };
  let result = await attempt();
  if (isTransportFailure(result.error)) {
    await sleep(200 + Math.floor(Math.random() * 250));
    result = await attempt();
  }
  const { data, error } = result;
  if (error) {
    const detail = error?.message || "Supabase RPC failed";
    throw new Error(label ? `${label}: ${detail}` : detail);
  }
  // The gate contract: an RPC gates itself and returns {error:true, message}
  // rather than throwing. Surface that message verbatim — it is user-facing
  // business text that tells the person what to do.
  if (data && typeof data === "object" && data.error === true) {
    throw new Error(data.message || `${name} returned an error`);
  }
  return data;
}

// Plain view / materialized-view read. Never null — an empty relation is [].
async function readRelation(relation, label) {
  const { data, error } = await supabase.from(relation).select("*");
  if (error) throw new Error(label ? `${label}: ${error.message}` : error.message);
  return Array.isArray(data) ? data : data ? [data] : [];
}

// ─── v_capability_coverage — one row per capability ──────────────────────────
export async function getCapabilityCoverage() {
  if (HUB_STUB) return vCapabilityCoverage;
  return readRelation("v_capability_coverage", "capability coverage");
}

// ─── mv_member_capability — who can do what (depth + recency) ────────────────
export async function getMemberCapability() {
  if (HUB_STUB) return mvMemberCapability;
  return readRelation("mv_member_capability", "member capability");
}

// ─── ops_capabilities — the catalogue (the rows; all grow) ───────────────────
export async function getCapabilitiesCatalogue() {
  if (HUB_STUB) return opsCapabilities;
  return readRelation("ops_capabilities", "capabilities catalogue");
}

// ─── fn_who_can — "who can run a Google Ad" with a reason ────────────────────
// p_capability is optional (omit → everyone ranked); p_client_id scopes to a
// client; p_limit bounds the result. Agents appear on identical terms — the
// profile is derived the same way, so "who can do this, person or agent" is one
// question. Returns whatever jsonb the function returns (per-rows array).
export async function whoCan({ capability = null, clientId = null, limit = 25 } = {}) {
  if (HUB_STUB) {
    if (!capability) return fnWhoCan;
    const needle = String(capability).toLowerCase();
    return fnWhoCan.filter((r) =>
      String(r.capability_key || r.capability || "")
        .toLowerCase()
        .includes(needle),
    );
  }
  const params = { p_limit: limit };
  if (capability) params.p_capability = capability;
  if (clientId) params.p_client_id = clientId;
  return capRpc("fn_who_can", params, "who can");
}